import React from 'react';

import { css, cx } from '@emotion/css';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { Skeleton } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TextToast } from '@/components/toast/TextToast.tsx';
import { usePatientIdFromURL } from '@/hooks/usePatientIdFromURL.ts';
import { useStyles } from '@/hooks/useStyles.ts';
import { Queries } from '@/queries/Queries.ts';
import { Form } from '@/uiKit/atoms/Form.tsx';
import { MessageAttachments } from '@/uiKit/molecules/chat/MessageAttachments.tsx';
import {
  MessageData,
  messageDataSchema,
} from '@/uiKit/molecules/chat/MessageDataSchema.ts';
import { MessageTextBox } from '@/uiKit/molecules/chat/MessageTextBox.tsx';

export const SEND_MESSAGE_FORM_ID = 'SEND_MESSAGE_FORM';

export type SendMessageFormProps = {
  className?: string;
};

export const SendMessageForm: React.FC<SendMessageFormProps> = ({
  className,
}) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const me = Queries.user.usePractitionerMe({});
  const sendMessage = Queries.messaging.useCreateMessage();
  const queryClient = useQueryClient();
  const patientId = usePatientIdFromURL();
  const draftMessage = Queries.messaging.useDraftMessage({
    patientId,
    practitionerId: me.id,
  });
  const updateDraftMessage = Queries.messaging.useUpdateDraftMessage();
  const isBillable = Queries.condition.useCondition(patientId, {
    select: data => data.isBillable,
  });

  const form = useForm<MessageData>({
    defaultValues: {
      // If the patient is not billable, there is no nurse, so the practitioner tab is disabled
      visibility: isBillable ? 'practitioners' : 'all',
      content_text: draftMessage.message ?? '',
      attachments: [],
    },
    resolver: standardSchemaResolver(messageDataSchema),
    criteriaMode: 'all',
    disabled: sendMessage.isPending,
  });

  return (
    <Form
      id={SEND_MESSAGE_FORM_ID}
      className={cx(styles.container, className)}
      onSubmit={({ attachments, ...data }) => {
        const files = (attachments ?? []).map(attachment => ({
          file: attachment,
          name: attachment.name,
        }));
        sendMessage.mutate(
          {
            patientId,
            ...data,
            attachments: files,
          },
          {
            onSuccess: () => {
              form.reset({
                visibility: data.visibility,
                content_text: '',
                attachments: [],
              });
              updateDraftMessage.mutate({
                patientId,
                practitionerId: me.id,
                message: undefined,
              });
            },
          },
        );
      }}
      onError={e => {
        if (e.attachments?.message) {
          TextToast.error(t(e.attachments.message));
        }
      }}
      formContext={form}
    >
      <div
        onMouseEnter={() =>
          Queries.messaging.useMessages.invalidate(queryClient, {
            patientId,
          })
        }
      >
        <MessageAttachments />
        <MessageTextBox />
      </div>
    </Form>
  );
};

export const SendMessageFormSkeleton = () => {
  return <Skeleton width="100%" height="calc(8px + 6rem + 31.75px + 3rem)" />;
};

const makeStyles = () => ({
  container: css({
    width: '100%',
  }),
});
